
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { SolicitudDetalle, SolicitudDetalleVenta, ADQUIRENTE_STYLES } from './types';

const fmt = (n: number) => `$${Math.abs(n).toLocaleString('es-CL')}`;

type EstadoSolicitud = SolicitudDetalle['estado'];

const NORMAL_STEPS = [
  { key: 'solicitada', label: 'Solicitada', icon: 'check' },
  { key: 'revision', label: 'En Revisión', icon: 'manage_search' },
  { key: 'aprobada', label: 'Aprobada', icon: 'verified' },
  { key: 'abonada', label: 'Abonada', icon: 'paid' },
];

const REJECTED_STEPS = [
  { key: 'solicitada', label: 'Solicitada', icon: 'check' },
  { key: 'revision', label: 'En Revisión', icon: 'manage_search' },
  { key: 'rechazada', label: 'Rechazada', icon: 'cancel' },
];

const CANCELLED_STEPS = [
  { key: 'solicitada', label: 'Solicitada', icon: 'check' },
  { key: 'cancelada', label: 'Cancelada', icon: 'do_not_disturb_on' },
];

function StatusStepper({ estado }: { estado: EstadoSolicitud }) {
  const isRejected = estado === 'rechazada';
  const isCancelled = estado === 'cancelada';
  const isTerminalNegative = isRejected || isCancelled;
  const steps = isRejected ? REJECTED_STEPS : isCancelled ? CANCELLED_STEPS : NORMAL_STEPS;

  const activeIndex = (() => {
    if (estado === 'revision') return 1;
    // Aprobada ya pasó; lo que sigue (Abonada) es lo que está en proceso.
    if (estado === 'aprobada') return 3;
    // Post-desembolso: el comercio ya recibió la plata. Cobranza interna
    // (en_cobro, cobrada, abandonada) se ve toda como Abonada completada.
    if (
      estado === 'abonada' ||
      estado === 'desembolsada' ||
      estado === 'en_cobro' ||
      estado === 'cobrada' ||
      estado === 'abandonada'
    )
      return 4;
    if (estado === 'rechazada') return 2;
    if (estado === 'cancelada') return 1;
    return 0;
  })();

  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 p-8 mb-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center w-full relative">
          {steps.map((step, i) => {
            const isCompleted = i < activeIndex;
            const isActive = i === activeIndex;
            const isPending = i > activeIndex;
            const isTerminalStep = isTerminalNegative && i === steps.length - 1;
            const isRejectedStep = isRejected && i === steps.length - 1;
            const isCancelledStep = isCancelled && i === steps.length - 1;

            return (
              <div key={step.key} className="flex items-center flex-1 last:flex-none">
                <div className="flex flex-col items-center z-10 w-24">
                  {isRejectedStep ? (
                    <div className="w-10 h-10 rounded-full bg-red-500 flex items-center justify-center ring-4 ring-red-500/20 shadow-md">
                      <span className="material-symbols-outlined text-white text-xl">close</span>
                    </div>
                  ) : isCancelledStep ? (
                    <div className="w-10 h-10 rounded-full bg-slate-500 flex items-center justify-center ring-4 ring-slate-500/20 shadow-md">
                      <span className="material-symbols-outlined text-white text-xl">{step.icon}</span>
                    </div>
                  ) : isCompleted ? (
                    <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center ring-4 ring-primary/20 shadow-md">
                      <span className="material-symbols-outlined text-white text-xl">check</span>
                    </div>
                  ) : isActive && !isTerminalStep ? (
                    <div className="relative w-10 h-10 rounded-full bg-white dark:bg-slate-800 border-4 border-primary flex items-center justify-center shadow-md ring-4 ring-primary/10">
                      <div className="w-3 h-3 rounded-full bg-primary animate-pulse" />
                    </div>
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-white dark:bg-slate-800 border-2 border-slate-200 dark:border-slate-700 flex items-center justify-center">
                      <span className="material-symbols-outlined text-slate-300 dark:text-slate-600">{step.icon}</span>
                    </div>
                  )}
                  <span
                    className={`text-xs mt-3 font-bold uppercase tracking-wide ${
                      isRejectedStep
                        ? 'text-red-500'
                        : isCancelledStep
                        ? 'text-slate-600 dark:text-slate-300'
                        : isCompleted || isActive
                        ? 'text-primary'
                        : 'text-slate-400'
                    } ${isPending && !isTerminalStep ? 'opacity-50' : ''}`}
                  >
                    {step.label}
                  </span>
                  {isCompleted && !isTerminalStep && (
                    <span className="text-[10px] text-slate-400 mt-1">Completado</span>
                  )}
                  {isActive && !isTerminalStep && (
                    <span className="text-[10px] text-slate-400 mt-1">En proceso</span>
                  )}
                  {isRejectedStep && (
                    <span className="text-[10px] text-red-400 mt-1">Rechazada</span>
                  )}
                  {isCancelledStep && (
                    <span className="text-[10px] text-slate-400 mt-1">Cancelada</span>
                  )}
                </div>
                {i < steps.length - 1 && (
                  <div
                    className={`h-1 flex-grow -mx-2 mb-8 ${
                      i < activeIndex
                        ? isRejected && i === steps.length - 2
                          ? 'bg-red-400'
                          : isCancelled && i === steps.length - 2
                          ? 'bg-slate-400'
                          : 'bg-primary'
                        : 'bg-slate-200 dark:bg-slate-700'
                    }`}
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function AdquirenteBadge({ adquirente }: { adquirente: string }) {
  const style = ADQUIRENTE_STYLES[adquirente] ?? { code: adquirente.slice(0, 2).toUpperCase(), bgColor: 'bg-slate-100', textColor: 'text-slate-600' };
  return (
    <div className="flex items-center gap-2">
      <div className={`w-6 h-6 rounded ${style.bgColor} flex items-center justify-center text-[10px] font-bold ${style.textColor}`}>
        {style.code}
      </div>
      <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">{adquirente}</span>
    </div>
  );
}

function VentaRow({ venta }: { venta: SolicitudDetalleVenta }) {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr className="bg-slate-50/80 dark:bg-slate-800/30 transition-colors">
        <td className="px-6 py-4">
          <button
            onClick={() => setExpanded(!expanded)}
            className="text-slate-400 hover:text-primary transition-colors"
          >
            <span
              className={`material-symbols-outlined text-lg text-slate-500 transition-transform duration-200 ${
                expanded ? 'rotate-0' : '-rotate-90'
              }`}
            >
              expand_circle_down
            </span>
          </button>
        </td>
        <td className="px-6 py-4 text-sm font-bold text-slate-700 dark:text-slate-300">{venta.fechaVenta}</td>
        <td className="px-6 py-4">
          <AdquirenteBadge adquirente={venta.adquirente} />
        </td>
        <td className="px-6 py-4 text-sm text-slate-600 dark:text-slate-400">{venta.cuotaDisplay}</td>
        <td className="px-6 py-4 text-sm font-semibold text-slate-700 dark:text-slate-300">{fmt(venta.abonoOriginal)}</td>
        <td className="px-6 py-4 text-sm text-slate-500">-{fmt(venta.comisionFlux)}</td>
        <td className="px-6 py-4 text-sm font-bold text-emerald-600 text-right">{fmt(venta.recibiras)}</td>
      </tr>
      {expanded && (
        <tr className="bg-slate-50/50 dark:bg-slate-800/10">
          <td colSpan={7} className="px-0 py-0">
            <div className="border-l-4 border-primary ml-10 my-2 overflow-hidden shadow-inner bg-white dark:bg-slate-900 rounded-r-lg">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-slate-400 font-semibold uppercase border-b border-slate-100 dark:border-slate-800">
                    <th className="py-3 pl-6 text-left text-primary">Detalle de la cuota</th>
                    <th className="py-3 text-left">Abono original</th>
                    <th className="py-3 text-left">Monto Original</th>
                    <th className="py-3 text-left">Días adelanto</th>
                    <th className="py-3 text-left">Comisión FLUX</th>
                    <th className="py-3 pr-6 text-right">Recibirá</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50 dark:divide-slate-800/50">
                  {venta.cuotas.map((c, idx) => (
                    <tr key={idx} className="hover:bg-slate-50 dark:hover:bg-slate-800/20">
                      <td className="py-3 pl-6 font-medium text-slate-700 dark:text-slate-300">{c.cuotaLabel}</td>
                      <td className="py-3 text-slate-500">{c.fechaAbono}</td>
                      <td className="py-3 font-medium text-slate-700 dark:text-slate-300">{fmt(c.montoOriginal)}</td>
                      <td className="py-3 text-slate-500">{c.diasAdelanto} días</td>
                      <td className="py-3 text-red-500">-{fmt(c.comisionFlux)}</td>
                      <td className="py-3 pr-6 text-right font-bold text-slate-700 dark:text-slate-200">{fmt(c.recibiras)}</td>
                    </tr>
                  ))}
                </tbody>
                <tfoot className="bg-slate-50/50 dark:bg-slate-800/20 border-t border-slate-100 dark:border-slate-800">
                  <tr>
                    <td colSpan={4} />
                    <td className="py-3 text-slate-500 font-medium text-right pr-4">Subtotal Venta:</td>
                    <td className="py-3 pr-6 text-right font-bold text-emerald-600">{fmt(venta.recibiras)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

interface Props {
  solicitud: SolicitudDetalle;
}

export default function SolicitudDetallePanel({ solicitud }: Props) {
  const idClean = solicitud.id.replace('#', '');

  return (
    <div className="py-8">
      <div className="mb-6 flex items-center gap-2">
        <Link
          to="/adelantos/solicitudes"
          className="text-slate-400 hover:text-primary transition-colors flex items-center gap-1 text-sm font-medium"
        >
          <span className="material-symbols-outlined text-lg">arrow_back</span>
          Volver a Mis Solicitudes
        </Link>
      </div>

      <StatusStepper estado={solicitud.estado} />

      {solicitud.estado === 'rechazada' && solicitud.motivoRechazo && (
        <div className="mb-8 flex items-start gap-4 p-5 rounded-xl border border-red-200 dark:border-red-900/40 bg-red-50 dark:bg-red-900/10">
          <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-500 flex-shrink-0 mt-0.5">
            <span className="material-symbols-outlined text-xl">report</span>
          </div>
          <div>
            <p className="text-sm font-bold text-red-700 dark:text-red-400 mb-1">Motivo del Rechazo</p>
            <p className="text-sm text-red-600 dark:text-red-300 leading-relaxed">{solicitud.motivoRechazo}</p>
          </div>
        </div>
      )}

      {solicitud.estado === 'cancelada' && (
        <div className="mb-8 flex items-start gap-4 p-5 rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
          <div className="w-10 h-10 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center text-slate-600 dark:text-slate-300 flex-shrink-0 mt-0.5">
            <span className="material-symbols-outlined text-xl">do_not_disturb_on</span>
          </div>
          <div>
            <p className="text-sm font-bold text-slate-700 dark:text-slate-200 mb-1">Solicitud cancelada</p>
            {solicitud.motivoRechazo ? (
              <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{solicitud.motivoRechazo}</p>
            ) : (
              <p className="text-sm text-slate-500 dark:text-slate-400 leading-relaxed">Cancelada por el comercio. Revertimos el débito de tu línea automáticamente.</p>
            )}
          </div>
        </div>
      )}

      <div id="solicitud-pdf-content" className="bg-white dark:bg-slate-900 rounded-xl shadow-sm border border-slate-200 dark:border-slate-800 overflow-hidden">
        <div className="p-6 border-b border-slate-200 dark:border-slate-800 flex flex-wrap justify-between items-center gap-4 bg-slate-50/50 dark:bg-slate-800/30">
          <div>
            <h2 className="text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <span className="material-symbols-outlined text-primary">receipt_long</span>
              Solicitud {solicitud.id}
            </h2>
            <p className="text-sm text-slate-500 mt-1">Resumen general de la operación</p>
          </div>
          <div className="flex gap-3 print:hidden">
            <button
              type="button"
              onClick={() => window.print()}
              className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-blue-700 transition-colors shadow-sm flex items-center gap-2"
            >
              <span className="material-symbols-outlined text-lg">download</span>
              Descargar PDF
            </button>
          </div>
        </div>

        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-start gap-4 p-4 rounded-lg border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/20">
            <div className="w-10 h-10 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-primary flex-shrink-0">
              <span className="material-symbols-outlined">tag</span>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">ID de Solicitud</p>
              <p className="text-lg font-bold text-slate-900 dark:text-white">{solicitud.id}</p>
            </div>
          </div>
          <div className="flex items-start gap-4 p-4 rounded-lg border border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/20">
            <div className="w-10 h-10 rounded-full bg-sky-50 dark:bg-sky-900/20 flex items-center justify-center text-sky-600 flex-shrink-0">
              <span className="material-symbols-outlined">calendar_today</span>
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Fecha Solicitud</p>
              <p className="text-lg font-bold text-slate-900 dark:text-white">{solicitud.fecha}</p>
            </div>
          </div>
          <div className="flex items-start gap-4 p-4 rounded-lg border border-emerald-100 dark:border-emerald-900/20 bg-emerald-50/50 dark:bg-emerald-900/10">
            <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600 flex-shrink-0">
              <span className="material-symbols-outlined">payments</span>
            </div>
            <div>
              <p className="text-xs font-semibold text-emerald-600/80 dark:text-emerald-400/80 uppercase tracking-wider">Total a Recibir</p>
              <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">{fmt(solicitud.totalTransferido)}</p>
            </div>
          </div>
        </div>

        <div className="border-t border-slate-200 dark:border-slate-800">
          <div className="px-6 py-5 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
            <h3 className="text-base font-bold text-slate-900 dark:text-white flex items-center gap-2">
              <span className="material-symbols-outlined text-primary">list_alt</span>
              Detalle de la Solicitud
            </h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-slate-400 text-xs font-semibold uppercase tracking-wider border-b border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/50">
                  <th className="px-6 py-3 w-10" />
                  <th className="px-6 py-3">Fecha Venta</th>
                  <th className="px-6 py-3">Adquirente</th>
                  <th className="px-6 py-3">Cuota</th>
                  <th className="px-6 py-3">Abono Original</th>
                  <th className="px-6 py-3">Comisión FLUX</th>
                  <th className="px-6 py-3 text-right">Recibirás</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {solicitud.ventas.map((venta) => (
                  <VentaRow key={venta.id} venta={venta} />
                ))}
              </tbody>
            </table>
          </div>

          <div className="p-6 bg-slate-50 dark:bg-slate-800/30 border-t border-slate-200 dark:border-slate-800 flex justify-end">
            <div className="w-full max-w-sm space-y-3">
              <div className="flex justify-between text-sm text-slate-600 dark:text-slate-400">
                <span>Subtotal de Operaciones</span>
                <span className="font-medium">{fmt(solicitud.subtotal)}</span>
              </div>
              <div className="flex justify-between text-sm text-slate-600 dark:text-slate-400">
                <span>Comisión Total Servicio</span>
                <span className="font-medium text-red-500">-{fmt(solicitud.comisionTotal)}</span>
              </div>
              {solicitud.tye != null && (
                <div className="flex justify-between text-sm text-slate-600 dark:text-slate-400">
                  <span>Timbre y Estampillas</span>
                  <span className="font-medium text-red-500">-{fmt(solicitud.tye)}</span>
                </div>
              )}
              <div className="border-t border-slate-200 dark:border-slate-700 my-2" />
              <div className="flex justify-between items-center">
                <span className="text-base font-bold text-slate-900 dark:text-white">Total Transferido</span>
                <span className="text-xl font-bold text-primary">{fmt(solicitud.totalTransferido)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
